import { useState } from "react";
import { useLoginState, LoginData } from "@auth/LoginState";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import { googleSignup, microsoftSignup } from "@libs/firebase";
import { getErrorMessage } from "@/studioxr/helper";
import { PrimaryButton, OutlineButton } from "@/studioxr/Button";
import {
  Typography,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Snackbar,
  Stack,
} from "@mui/material";
import {
  Google as GoogleIcon,
  Microsoft as MicrosoftIcon,
} from "@mui/icons-material";
import PasswordField from "./PasswordField";
import { authRedirect } from "../utils";

export default function SignupForm({ onSuccess }) {
  const setShowLogin = useLoginState((state) => state.setShowLogin);
  const setOrCreateProfile = useLoginState((state) => state.setOrCreateProfile);
  const [flashMessage, setFlashMessage] = useState<string | null>(null);
  const [emailValue, setEmailValue] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [mailingList, setMailingList] = useState(true);
  const [openSnack, setOpenSnack] = useState(false);

  const signupButtonLabel = "Create Account";
  const auth = getAuth();

  const onChangeEmail = (e) => {
    setEmailValue(e.target.value.replace(/\s/g, ""));
  };
  const onChangePassword = (e) => {
    setPassword(e.target.value);
  };
  const onChangePassword2 = (e) => {
    setPassword2(e.target.value);
  };
  const handleMailingList = (e) => {
    setMailingList(e.target.checked);
  };
  const [signUpButtonDetails, setSignUpButtonDetails] = useState({
    label: signupButtonLabel,
    disable: false,
  });
  const [signUpWithGoogleButtonDetails, setSignUpWithGoogleButtonDetails] =
    useState({ label: "Sign Up With Google", disable: false });
  const [
    signUpWithMicrosoftButtonDetails,
    setSignUpWithMicrosoftButtonDetails,
  ] = useState({ label: "Sign Up With Microsoft", disable: false });

  const validateSubmit = () => {
    if (!emailValue || !password || !password2) {
      setFlashMessage("Please enter all the required details.");
      return false;
    } else if (password !== password2) {
      setFlashMessage("Passwords do not match!");
      return false;
    }
  };

  const disableWhileLoading = () => {
    setFlashMessage(null);
    setSignUpWithGoogleButtonDetails({
      label: "Signing Up...Please Wait",
      disable: true,
    });
    setSignUpWithMicrosoftButtonDetails({
      label: "Signing Up...Please Wait",
      disable: true,
    });
    setSignUpButtonDetails({
      label: "Signing Up...Please Wait",
      disable: true,
    });
  };

  const reenableButtons = () => {
    setSignUpWithGoogleButtonDetails({
      label: "Sign Up With Google",
      disable: false,
    });
    setSignUpWithMicrosoftButtonDetails({
      label: "Sign Up With Microsoft",
      disable: false,
    });
    setSignUpButtonDetails({
      label: signupButtonLabel,
      disable: false,
    });
  };

  const handleProfileCreation = async (userCredential) => {
    try {
      setOpenSnack(true);

      const loginData: LoginData = {
        user: userCredential.user,
        providerId: userCredential.user.providerId || "password",
        _tokenResponse: (userCredential as any)._tokenResponse || null,
      };

      const result: any = await setOrCreateProfile(loginData, mailingList);

      if (result?.updated === false) {
        setShowLogin(null);
        if (authRedirect(window.location.pathname)) {
          return;
        }
      }
      onSuccess?.();
    } catch (error) {
      reenableButtons();
      const errMsg =
        getErrorMessage(error.message) +
        " - Please contact support@mattoboard.com if you need help.";
      setFlashMessage(errMsg);
    } finally {
      setOpenSnack(false);
    }
  };

  const handleSubmit = async (evt) => {
    evt?.preventDefault();
    if (validateSubmit() === false) {
      return;
    }
    disableWhileLoading();

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        emailValue,
        password,
      );
      return handleProfileCreation(userCredential);
    } catch (error) {
      reenableButtons();
      setFlashMessage("Error: " + getErrorMessage(error.message));
      return;
    }
  };

  const handleGoogleSignup = async (evt) => {
    evt?.preventDefault();
    disableWhileLoading();

    try {
      const userCredential = await googleSignup();
      return handleProfileCreation(userCredential);
    } catch (error) {
      reenableButtons();
      setFlashMessage("Error: " + getErrorMessage(error.message));
      return;
    }
  };

  const handleMicrosoftSignup = async (evt) => {
    evt?.preventDefault();
    disableWhileLoading();

    try {
      const userCredential = await microsoftSignup();
      return handleProfileCreation(userCredential);
    } catch (error) {
      reenableButtons();
      setFlashMessage("Error: " + getErrorMessage(error.message));
      return;
    }
  };

  return (
    <>
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <img
          src={"/images/mattoboard-logo.jpg"}
          alt="MattoBoard"
          className="logo-img"
        />
        <Typography component="h1" variant="h5" className="login-title">
          Sign up To Continue
        </Typography>
      </Box>
      {flashMessage && (
        <Typography
          sx={{ fontSize: "1rem" }}
          component="h1"
          color="error"
          align="center"
          variant="h5"
        >
          {flashMessage}
        </Typography>
      )}
      <Box
        component="form"
        onSubmit={handleSubmit}
        className="login-form"
        noValidate
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flex: "1 1 auto" }}>
          <OutlineButton
            onClick={() => {
              handleGoogleSignup(null);
            }}
            fullWidth
            variant="outlined"
            className="google-btn"
            style={{ marginTop: "5px", marginBottom: "15px" }}
            disabled={signUpWithGoogleButtonDetails.disable}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
            >
              <GoogleIcon sx={{ paddingTop: "0px", color: "#453B41" }} />
              <Typography sx={{ fontSize: "0.875rem" }}>
                {signUpWithGoogleButtonDetails.label}
              </Typography>
            </Stack>
          </OutlineButton>

          <OutlineButton
            onClick={() => {
              handleMicrosoftSignup(null);
            }}
            fullWidth
            variant="outlined"
            className="microsoft-btn"
            style={{ marginTop: "5px", marginBottom: "15px" }}
            disabled={signUpWithMicrosoftButtonDetails.disable}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
            >
              <MicrosoftIcon sx={{ paddingTop: "0px", color: "#4285F4" }} />
              <Typography sx={{ fontSize: "0.875rem" }}>
                {signUpWithMicrosoftButtonDetails.label}
              </Typography>
            </Stack>
          </OutlineButton>

          <Typography sx={{ textAlign: "center" }}>or</Typography>

          <TextField
            size="small"
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="email"
            type="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            value={emailValue}
            onChange={onChangeEmail}
          />
          <PasswordField
            name="password"
            label="Password"
            value={password}
            onChange={onChangePassword}
          />
          <PasswordField
            name="password2"
            label="Confirm Password"
            value={password2}
            onChange={onChangePassword2}
          />
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={mailingList}
                  onChange={handleMailingList}
                  name="mailingList"
                />
              }
              label="I agree to join Mattoboard's mailing List"
            />
          </FormGroup>

          <PrimaryButton
            type="submit"
            style={{ marginTop: "15px" }}
            fullWidth
            variant="contained"
            disabled={signUpButtonDetails.disable}
          >
            {signUpButtonDetails.label}
          </PrimaryButton>

          <Box className="signup-link" sx={{ paddingTop: "15px" }}>
            <Typography className="signup-text">
              Already have an account?
            </Typography>
            <button
              style={{ color: "blue", fontWeight: "400" }}
              className="signup-btn"
              onClick={() => setShowLogin("signin")}
            >
              Sign in
            </button>
          </Box>
        </Box>
        
        <Box
          sx={{
            textAlign: "left",
            pt: 2,
            pb: 2,
            marginTop: "auto",
          }}
        >
          <Typography variant="caption" className="signup-text">
            By signing up you agree to the{" "}
            <a
              href="https://blog.mattoboard.com/terms-of-use"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href="https://blog.mattoboard.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
          </Typography>
        </Box>
      </Box>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        open={openSnack}
        onClose={() => {
          setOpenSnack(false);
        }}
        autoHideDuration={6000}
        message="Signing Up.."
      />
    </>
  );
}
